import { Alert, Anchor, Button, Grid, Group, SegmentedControl, Stack, Text, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconSend } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, podatki } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { Pogovor } from '../povratne/Pogovor';
import { statusPovratne } from '../povratne/povratne';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import { Oznaka } from '../strani/PregledRacunov';
import { SkrbniskiZaslon } from './Skrbnistvo';
import { cas } from './skrbnistvo';
import { glave, obNapaki } from './zeton';

const STANJA = [
  { value: 'NOVO', label: 'Novo' },
  { value: 'OGLEDANO', label: 'Ogledano' },
  { value: 'ZAKLJUCENO', label: 'Zaključeno' },
];

/**
 * Skrbništvo → povratna informacija: pogovor s stranko, odgovor (stranka ga vidi v aplikaciji in dobi po e-pošti) in
 * stanje NOVO / OGLEDANO / ZAKLJUCENO. Odgovor brez spremembe stanja NOVO premakne v OGLEDANO; »Odgovori in zaključi«
 * oboje naenkrat. Vsako dejanje je v dnevniku skrbništva.
 */
export function PovratnaSkrbnistvo() {
  return (
    <SkrbniskiZaslon>
      <Povratna />
    </SkrbniskiZaslon>
  );
}

function Povratna() {
  const id = Number(useParams().id);
  const odjemalec = useQueryClient();
  const poizvedba = useQuery({
    queryKey: ['skrbnik', 'povratna', id],
    queryFn: async ({ signal }) => {
      try {
        return await podatki(
          api.GET('/api/skrbnik/povratne-informacije/{id}', { signal, headers: glave(), params: { path: { id } } }),
        );
      } catch (e) {
        obNapaki(e);
        throw e;
      }
    },
  });
  const [besedilo, setBesedilo] = useState('');
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState<string | null>(null);

  const p = poizvedba.data;
  if (!p) {
    return (
      <StanjeNalaganja
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        naslov="Povratne informacije ni bilo mogoče naložiti"
      />
    );
  }

  /** Pošlje odgovor in/ali stanje; {@code kaj} pove, kateri gumb se vrti. */
  const poslji = async (kaj: string, odgovor: string | null, status: string | null) => {
    setNapake({});
    setNapaka(null);
    setDela(kaj);
    try {
      const n = await podatki(
        api.POST('/api/skrbnik/povratne-informacije/{id}/odgovor', {
          headers: glave(),
          params: { path: { id } },
          body: { besedilo: odgovor ?? undefined, status: status ?? undefined },
        }),
      );
      odjemalec.setQueryData(['skrbnik', 'povratna', id], n);
      await odjemalec.invalidateQueries({ queryKey: ['skrbnik', 'povratne'] });
      await odjemalec.invalidateQueries({ queryKey: ['skrbnik', 'povratne-stevilo'] });
      if (odgovor) {
        setBesedilo('');
      }
      notifications.show({
        color: 'green',
        message: odgovor ? 'Odgovor je poslan.' : `Stanje: ${statusPovratne(n.status).naziv}.`,
      });
    } catch (e) {
      obNapaki(e);
      if (e instanceof NapakaApi && Object.keys(e.polja).length) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(null);
    }
  };

  const s = statusPovratne(p.status);
  return (
    <ZaslonSeznama>
      <Button
        component={Link}
        to="/skrbnistvo?zavihek=povratne"
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        mb="xs"
        px={0}
      >
        Povratne informacije
      </Button>
      <GlavaZaslona naslov={`Povratna informacija #${p.id}`} opis={`${p.instanca} · ${p.avtor}`} />
      <Group gap={6} mb="sm" mt={-8}>
        <Oznaka barva={s.barva}>{s.naziv}</Oznaka>
      </Group>
      {napaka && (
        <Alert color="red" variant="light" mb="md" role="alert">
          {napaka}
        </Alert>
      )}
      <Grid gap="md">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="md">
            <Kartica naslov="Pogovor">
              <Pogovor sporocila={p.sporocila ?? []} desno={(m) => !!m.skrbnik} />
            </Kartica>
            <Kartica naslov="Odgovor">
              <Textarea
                aria-label="Odgovor"
                placeholder="Stranka vidi odgovor v aplikaciji in ga prejme po e-pošti."
                value={besedilo}
                onChange={(e) => setBesedilo(e.currentTarget.value)}
                error={napake.besedilo}
                autosize
                minRows={4}
                maxRows={14}
              />
              <Group justify="flex-end" mt="md" gap="sm">
                <Button
                  variant="default"
                  onClick={() => void poslji('zakljuci', besedilo, 'ZAKLJUCENO')}
                  loading={dela === 'zakljuci'}
                  disabled={dela !== null}
                >
                  Odgovori in zaključi
                </Button>
                <Button
                  leftSection={<IconSend size={18} />}
                  onClick={() => void poslji('odgovor', besedilo, null)}
                  loading={dela === 'odgovor'}
                  disabled={dela !== null}
                >
                  Odgovori
                </Button>
              </Group>
            </Kartica>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="md">
            <Kartica naslov="Stanje">
              <SegmentedControl
                aria-label="Stanje"
                fullWidth
                data={STANJA}
                value={p.status ?? 'NOVO'}
                disabled={dela !== null}
                onChange={(v) => void poslji('stanje', null, v)}
              />
              {napake.status && (
                <Text size="sm" c="red" mt={4}>
                  {napake.status}
                </Text>
              )}
            </Kartica>
            <Kartica naslov="Podrobnosti">
              <Podatki
                vrstice={[
                  [
                    'Instanca',
                    <Anchor key="i" component={Link} to={`/skrbnistvo/${p.companyId}`} size="sm">
                      {p.instanca} (#{p.companyId})
                    </Anchor>,
                  ],
                  ['Poslal', p.avtor],
                  ['Poslano', cas(p.ustvarjeno)],
                  ['Zadnje sporočilo', cas(p.zadnjeSporocilo)],
                  ['Stanje spremenil', p.statusSpremenil ? `${p.statusSpremenil}, ${cas(p.statusSpremenjeno)}` : ''],
                ]}
              />
            </Kartica>
          </Stack>
        </Grid.Col>
      </Grid>
    </ZaslonSeznama>
  );
}
